import { supabase } from "../../lib/supabase";

/* ---------------------------------------------------------------- types */

export interface CompanySummary {
  id: number;
  name: string;
  legal_name: string | null;
  customer_ref: string | null;
  website: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  postcode: string | null;
  payment_terms: string | null;
  notes: string | null;
  source: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  legacy_company_id: number | null;
  legacy_quote_client_id: number | null;
  legacy_client_id: number | null;
  site_count: number;
  contact_count: number;
  contract_count: number;
  /** Masked to null for callers without can_view_sla_costs (see v_companies_summary, migration 0174). */
  active_contract_value: number | null;
  next_contract_end_date: string | null;
}

export interface Site {
  id: number;
  company_id: number;
  name: string;
  address: string | null;
  city: string | null;
  postcode: string | null;
  lead_contact_id: number | null;
  contact_notes: string | null;
  pm_last_email_sent: string | null;
  pm_arranged_by: string | null;
  pm_client_confirmed: boolean;
  pm_next_visit_booked_at: string | null;
  notes: string | null;
  archived_at: string | null;
}

export interface SiteInput {
  company_id: number;
  name: string;
  address: string | null;
  city: string | null;
  postcode: string | null;
  notes: string | null;
  /** Set from the verified address pick so the site plots on the PM map.
   * Null when the office was added via the manual-entry escape hatch. */
  lat?: number | null;
  lng?: number | null;
}

/** No new migration needed -- sites' write RLS (can_edit_contracts, same as
 * contracts/contacts) was already in place; there was simply no UI path to
 * reach it (audit finding: no way to create a Site anywhere in the app).
 * geocoded_at is stamped here when coordinates came from a verified pick. */
export async function createSite(input: SiteInput): Promise<number> {
  const row = {
    ...input,
    geocoded_at: input.lat != null && input.lng != null ? new Date().toISOString() : null,
  };
  const { data, error } = await supabase.from("sites").insert(row).select("id").single();
  if (error) throw error;
  return data.id;
}

export interface Contact {
  id: number;
  company_id: number;
  site_id: number | null;
  name: string;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  department: string | null;
  is_primary: boolean;
  is_support: boolean;
  is_renewal: boolean;
  is_technical: boolean;
  /** Client-wide sales relevance — separate from is_support (service relevance). */
  is_sales: boolean;
  /** At most one true per company (0165) — the default service contact absent a site-specific override. */
  is_default_service_contact: boolean;
  /** At most one true per company (0165) — the default sales contact. */
  is_default_sales_contact: boolean;
  notes: string | null;
  archived_at: string | null;
}

export interface Contract {
  id: number;
  company_id: number;
  reference: string | null;
  /** Null unless the caller holds can_view_sla_costs — v_contracts_display masks it, not RLS row-filtering. */
  job_number: string | null;
  cover_level: "gold" | "silver" | "bronze";
  /** Null unless the caller holds can_view_sla_costs. */
  value: number | null;
  start_date: string | null;
  end_date: string | null;
  length_years: number | null;
  renewal_contact_id: number | null;
  not_renewing: boolean;
  not_renewing_reason: string | null;
  last_chase_sent_at: string | null;
  sla_exclusions: string | null;
  notes: string | null;
  archived_at: string | null;
}

export interface ContractProfitability {
  contract_id: number;
  cover_level: "gold" | "silver" | "bronze";
  not_renewing: boolean;
  contract_length_years: number;
  annual_value: number;
  lifetime_value: number;
  total_cost: number;
  cost_txn_count: number;
  profit_loss: number;
  margin_pct: number | null;
}

export interface CompanyDetail {
  company: CompanySummary;
  sites: Site[];
  contacts: Contact[];
  contracts: Contract[];
}

/* ---------------------------------------------------------------- reads */

export async function fetchCompanies(): Promise<CompanySummary[]> {
  const { data, error } = await supabase
    .from("v_companies_summary")
    .select("*")
    .is("archived_at", null)
    .order("name");
  if (error) throw error;
  return (data ?? []) as CompanySummary[];
}

export async function fetchCompanyDetail(companyId: number): Promise<CompanyDetail> {
  const [companyRes, sitesRes, contactsRes, contractsRes] = await Promise.all([
    supabase.from("v_companies_summary").select("*").eq("id", companyId).maybeSingle(),
    supabase.from("sites").select("*").eq("company_id", companyId).is("archived_at", null).order("name"),
    supabase.from("contacts").select("*").eq("company_id", companyId).is("archived_at", null).order("name"),
    // v_contracts_display, not the bare table: this screen is reachable by
    // any can_view_service holder, and value/job_number must stay masked
    // for anyone without can_view_sla_costs (Service migration plan §2).
    supabase.from("v_contracts_display").select("*").eq("company_id", companyId).is("archived_at", null).order("end_date"),
  ]);
  if (companyRes.error) throw companyRes.error;
  if (sitesRes.error) throw sitesRes.error;
  if (contactsRes.error) throw contactsRes.error;
  if (contractsRes.error) throw contractsRes.error;

  return {
    company: companyRes.data as CompanySummary,
    sites: (sitesRes.data ?? []) as Site[],
    contacts: (contactsRes.data ?? []) as Contact[],
    contracts: (contractsRes.data ?? []) as Contract[],
  };
}

/** Gated by can_view_sla_costs at the row level (v_sla_profitability) — comes back empty otherwise. */
export async function fetchContractProfitability(companyId: number): Promise<ContractProfitability[]> {
  const { data, error } = await supabase
    .from("v_sla_profitability")
    .select("*")
    .eq("company_id", companyId)
    .order("contract_id");
  if (error) throw error;
  return (data ?? []) as ContractProfitability[];
}

/* --------------------------------------------------------------- writes */

export interface CompanyEdit {
  name: string;
  legal_name: string | null;
  customer_ref: string | null;
  website: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  postcode: string | null;
  payment_terms: string | null;
  notes: string | null;
}

export async function updateCompany(companyId: number, patch: CompanyEdit): Promise<void> {
  const { error } = await supabase.from("companies").update(patch).eq("id", companyId);
  if (error) throw error;
}

/** Create a customer from the Customers list. source='manual' marks it as a
 * hand-entered record (not a Glide import or the old synthetic seed). Returns
 * the new company id so the caller can open it. Gated by RLS on
 * can_edit_contracts, same as updateCompany. */
export async function createCompany(input: CompanyEdit): Promise<number> {
  const { data, error } = await supabase
    .from("companies")
    .insert({ ...input, source: "manual" })
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  return data.id as number;
}

export interface ContactInput {
  company_id: number;
  site_id: number | null;
  name: string;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  is_primary: boolean;
  is_support: boolean;
  is_renewal: boolean;
  is_technical: boolean;
  is_sales?: boolean;
  is_default_service_contact?: boolean;
  is_default_sales_contact?: boolean;
}

export async function createContact(input: ContactInput): Promise<void> {
  const { error } = await supabase.from("contacts").insert(input);
  if (error) throw error;
}

export async function updateContact(contactId: number, patch: Partial<ContactInput>): Promise<void> {
  const { error } = await supabase.from("contacts").update(patch).eq("id", contactId);
  if (error) throw error;
}

export async function archiveContact(contactId: number): Promise<void> {
  const { error } = await supabase
    .from("contacts")
    .update({ archived_at: new Date().toISOString() })
    .eq("id", contactId);
  if (error) throw error;
}
